import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface CourseDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onCourseChange?: (courseCode: string) => void; // Callback for when course changes
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
  courseLevelCode?: string; // Optional: filter courses by course level
}

const CourseDropdown: React.FC<CourseDropdownProps> = ({
  value,
  onChange = () => {},
  placeholder = "Select Course",
  disabled = false,
  className = '',
  onCourseChange,
  onDropdownLoad,
  courseLevelCode
}) => {
  const [courseOptions, setCourseOptions] = useState<{ key: string, label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCourseOptions();
    // eslint-disable-next-line
  }, [courseLevelCode]);

  const loadCourseOptions = async () => {
    setLoading(true);
    try {
      let courses;
      if (courseLevelCode) {
        courses = await FiltersService.getDropdownItems(DROPDOWN_CODES.Course, courseLevelCode);
      } else {
        courses = await FiltersService.getDropdownItems(DROPDOWN_CODES.Course);
      }
      setCourseOptions([...courses.map(item => ({
        key: (item.value || '').trim().toUpperCase(),
        label: item.label || ''
      }))]);
      if (onDropdownLoad) onDropdownLoad();
    } catch (error) {
      console.error('Failed to load course options:', error);
      message.error('Failed to load course options');
      if (onDropdownLoad) onDropdownLoad();
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (courseCode: string) => {
    onChange(courseCode);
    if (onCourseChange) {
      onCourseChange(courseCode);
    }
  };

  // DEBUG LOG for dropdown value debugging
  console.log('CourseDropdown value:', value, 'options:', courseOptions);

  return (
    <SimpleDropdown
      options={courseOptions}
      value={value}
      onChange={handleCourseChange}
      placeholder={loading ? "Loading..." : placeholder}
      disabled={disabled || loading}
    />
  );
};

export default CourseDropdown; 